import { selectedGroups } from "./studioGroups";
import React, { useMemo } from "react";
import { getStyle, resolveText } from "./studioModel";
import { textRuns } from "./studioMeasure";
import HeadingVine from "./HeadingVine";
import { inheritedHeadingTiltCorrection } from "./studioArtwork";

export { textLines } from "./studioMeasure";

export function NodeArtwork({ node, doc, content }) {
  const s = useMemo(() => getStyle(doc, node), [doc, node]);
  // Exactly the runs every export draws: preserved imported runs while they
  // still apply, otherwise freshly wrapped lines (shared core).
  const layout = useMemo(
    () => (node.type === "text" ? textRuns(node, s, String(resolveText(node, content) ?? "")) : null),
    [node, content, s]
  );
  const useOriginal = !!layout?.original;
  const original = node.originalLayout;
  const lines = layout && !layout.original ? layout.runs.map((r) => r.text) : [];
  const textX =
    s.align === "center" ? node.w / 2 : s.align === "right" ? node.w : 0;
  const headingTiltCorrection = inheritedHeadingTiltCorrection(node);
  return (
    <g opacity={node.opacity}>
      {node.type === "text" && (
        <text
          x={textX}
          y={s.fontSize * 0.86}
          fill={s.fill}
          fontFamily={s.fontFamily}
          fontSize={s.fontSize}
          fontWeight={s.fontWeight}
          fontStyle={s.fontStyle}
          letterSpacing={s.letterSpacing}
          textAnchor={
            s.align === "center"
              ? "middle"
              : s.align === "right"
              ? "end"
              : "start"
          }
          style={{
            whiteSpace: "pre",
            textTransform: s.textTransform === "titlecase" ? undefined : s.textTransform,
            fontVariant: s.fontVariant,
            fontFeatureSettings: s.fontFeatureSettings,
          }}
        >
          {useOriginal
            ? original.runs.map((run, i) => (
                <tspan key={i} x={run.x} y={run.y} textAnchor="start">
                  {run.text}
                </tspan>
              ))
            : lines.map((line, i) => (
                <tspan
                  key={i}
                  x={textX}
                  dy={i === 0 ? 0 : s.fontSize * s.lineHeight}
                >
                  {line || " "}
                </tspan>
              ))}
        </text>
      )}
      {node.type === "rect" && (
        <rect
          width={node.w}
          height={node.h}
          rx={s.radius || 0}
          fill={s.fill}
          stroke={s.stroke || "none"}
          strokeWidth={s.strokeWidth || 0}
        />
      )}
      {node.type === "ellipse" && (
        <ellipse
          cx={node.w / 2}
          cy={node.h / 2}
          rx={node.w / 2}
          ry={node.h / 2}
          fill={s.fill}
          stroke={s.stroke || "none"}
          strokeWidth={s.strokeWidth || 0}
        />
      )}
      {node.type === "line" && (
        <line
          x1="0"
          y1={node.h / 2}
          x2={node.w}
          y2={node.h / 2}
          stroke={s.fill}
          strokeWidth={s.strokeWidth || 1}
          strokeDasharray={s.strokeDasharray}
          strokeLinecap={s.strokeLinecap}
        />
      )}
      {node.type === "image" && node.headingWinglet && !node.headingWinglet.customArtwork && (
        <HeadingVine width={node.w} height={node.h} side={node.headingWinglet.side} />
      )}
      {node.type === "image" && (!node.headingWinglet || node.headingWinglet.customArtwork) && (
        <image
          href={node.src}
          width={node.w}
          height={node.h}
          transform={headingTiltCorrection
            ? `rotate(${headingTiltCorrection} ${node.w / 2} ${node.h / 2})`
            : undefined}
          preserveAspectRatio={
            node.fit === "cover" ? "xMidYMid slice" : "xMidYMid meet"
          }
        />
      )}
    </g>
  );
}
export default function StudioCanvas({
  doc,
  page,
  content,
  selected = [],
  focusedLayerId,
  dragHiddenIds = [],
  zoom = 1,
  grid,
  margins,
  guides = [],
  onNodeDown,
  onBackgroundDown,
  onEdit,
  editingId,
  editingIds = [],
  onResize,
  onRotate,
  svgRef,
  preview = false,
  selectionOnly = false,
  marquee,
  gaps = [],
  onGapDown,
}) {
  const groupOutlines = useMemo(() => {
    if (preview || selectionOnly) return [];
    return selectedGroups(page, selected, focusedLayerId)
      .map(({ id, members }) => {
        // Rotation happens around each element's center, as in NodeArtwork.
        const corners = members.flatMap((n) => {
          const angle = (n.rotation * Math.PI) / 180;
          const cos = Math.cos(angle),
            sin = Math.sin(angle);
          return [
            [0, 0],
            [n.w, 0],
            [0, n.h],
            [n.w, n.h],
          ].map(([x, y]) => ({
            x: n.x + n.w / 2 + (x - n.w / 2) * cos - (y - n.h / 2) * sin,
            y: n.y + n.h / 2 + (x - n.w / 2) * sin + (y - n.h / 2) * cos,
          }));
        });
        const x = Math.min(...corners.map((p) => p.x));
        const y = Math.min(...corners.map((p) => p.y));
        return {
          id,
          members,
          x,
          y,
          width: Math.max(...corners.map((p) => p.x)) - x,
          height: Math.max(...corners.map((p) => p.y)) - y,
        };
      });
  }, [page, selected, focusedLayerId, preview, selectionOnly]);
  const groupedNodes = new Set(
    groupOutlines.flatMap((g) => g.members.map((n) => n.id))
  );
  return (
    <svg
      ref={svgRef}
      xmlns="http://www.w3.org/2000/svg"
      className="ms-paper"
      width={page.width * zoom}
      height={page.height * zoom}
      viewBox={`0 0 ${page.width} ${page.height}`}
      style={{ background: page.background }}
      onPointerDown={onBackgroundDown}
      aria-label={`${page.name} design canvas`}
      role="img"
    >
      {doc.fonts?.length > 0 && (
        <style>
          {doc.fonts
            .map(
              (font) =>
                `@font-face{font-family:"${font.family}";font-style:${font.style};font-weight:${font.weight};src:url(${font.src})}`
            )
            .join("\n")}
        </style>
      )}
      <rect width={page.width} height={page.height} fill={page.background} />
      {!preview && grid && (
        <g pointerEvents="none" opacity="0.18">
          {Array.from({ length: Math.ceil(page.width / 12) }, (_, i) => (
            <path
              key={`x${i}`}
              d={`M${i * 12} 0V${page.height}`}
              stroke="#887f70"
              strokeWidth="0.5"
            />
          ))}
          {Array.from({ length: Math.ceil(page.height / 12) }, (_, i) => (
            <path
              key={`y${i}`}
              d={`M0 ${i * 12}H${page.width}`}
              stroke="#887f70"
              strokeWidth="0.5"
            />
          ))}
        </g>
      )}
      {!preview && margins && (
        <rect
          x={page.margin}
          y={page.margin}
          width={Math.max(0, page.width - page.margin * 2)}
          height={Math.max(0, page.height - page.margin * 2)}
          fill="none"
          stroke="#ac9775"
          strokeWidth={0.7 / zoom}
          strokeDasharray="4 4"
          pointerEvents="none"
        />
      )}
      {page.elements
        .filter((n) => !n.hidden)
        .map((node) => (
          <g
            key={node.id}
            data-node-id={node.id}
            visibility={dragHiddenIds.includes(node.id) ? "hidden" : undefined}
            transform={`translate(${node.x} ${node.y}) rotate(${
              node.rotation
            } ${node.w / 2} ${node.h / 2})`}
            onPointerDown={(e) => onNodeDown?.(e, node)}
            onDoubleClick={(e) => {
              e.stopPropagation();
              onEdit?.(node);
            }}
            style={{
              cursor: preview
                ? "default"
                : node.locked
                ? "not-allowed"
                : "move",
            }}
          >
            {editingId !== node.id && !editingIds.includes(node.id) && <NodeArtwork node={node} doc={doc} content={content} />}
            {!preview && (
              <rect
                width={node.w}
                height={node.h}
                fill="transparent"
                stroke={
                  editingId !== node.id && !editingIds.includes(node.id) && selected.includes(node.id) && !groupedNodes.has(node.id)
                    ? "#a5712b"
                    : "none"
                }
                strokeWidth={1.2 / zoom}
              />
            )}
            {!preview &&
              !selectionOnly &&
              editingId !== node.id &&
              !editingIds.includes(node.id) &&
              !groupedNodes.has(node.id) &&
              selected.includes(node.id) &&
              !node.locked && (
                <g>
                  {["nw", "ne", "sw", "se"].map((corner) => (
                    <rect
                      key={corner}
                      x={(corner.includes("e") ? node.w : 0) - 4 / zoom}
                      y={(corner.includes("s") ? node.h : 0) - 4 / zoom}
                      width={8 / zoom}
                      height={8 / zoom}
                      fill="white"
                      stroke="#a5712b"
                      strokeWidth={1 / zoom}
                      style={{ cursor: `${corner}-resize` }}
                      onPointerDown={(e) => onResize?.(e, node, corner)}
                    />
                  ))}
                  {selected.length === 1 && (
                    <g
                      onPointerDown={(e) => onRotate?.(e, node)}
                      style={{ cursor: "grab" }}
                    >
                      <line
                        x1={node.w / 2}
                        x2={node.w / 2}
                        y1="0"
                        y2={-20 / zoom}
                        stroke="#a5712b"
                        strokeWidth={1 / zoom}
                      />
                      <circle
                        cx={node.w / 2}
                        cy={-23 / zoom}
                        r={5 / zoom}
                        fill="white"
                        stroke="#a5712b"
                        strokeWidth={1 / zoom}
                      />
                    </g>
                  )}
                </g>
              )}
          </g>
        ))}
      {groupOutlines.map(({ id, x, y, width, height }) => (
        <rect
          key={id}
          data-selection-group={id}
          x={x}
          y={y}
          width={width}
          height={height}
          fill="none"
          stroke="#a5712b"
          strokeWidth={1.2 / zoom}
          pointerEvents="none"
        />
      ))}
      {!preview &&
        guides.map((g, i) => (
          <path
            key={i}
            d={
              g.axis === "x"
                ? `M${g.value} 0V${page.height}`
                : `M0 ${g.value}H${page.width}`
            }
            stroke="#b96939"
            strokeWidth={0.8 / zoom}
            strokeDasharray="4 3"
            pointerEvents="none"
          />
        ))}
      {!preview && gaps.map((g) => (
        // Spacing handle: drag to change this gap; Shift-drag changes every gap in the section.
        <g key={g.key} className="ms-gap-handle" data-gap-key={g.key} onPointerDown={(e) => onGapDown?.(e, g)} style={{ cursor: "ns-resize" }}>
          <rect x={g.x} y={g.y} width={g.w} height={Math.max(g.gap, 4 / zoom)} fill="#d3963a22" stroke="#d3963a" strokeWidth={0.6 / zoom} strokeDasharray={`${3 / zoom} ${2 / zoom}`} />
          <text x={g.x + g.w + 4 / zoom} y={g.y + Math.max(g.gap, 4 / zoom) / 2 + 3 / zoom} fontSize={9 / zoom} fill="#d3963a" fontFamily="system-ui, sans-serif">{Math.round(g.gap * 10) / 10} pt</text>
        </g>
      ))}
      {marquee && (
        <rect
          {...marquee}
          width={marquee.w}
          height={marquee.h}
          fill="#a5712b15"
          stroke="#a5712b"
          strokeWidth={1 / zoom}
          pointerEvents="none"
        />
      )}
    </svg>
  );
}
